// 📤 업로드 전 파일 안정화 (2026-09-22 원격 "안드앱에서 사진 업로드 안됨. 갤러리 업로드도 안되고. 아이폰에서도 그랬는데")
//   규명: nginx 가 rooms.upload 에 **400·본문 0바이트** 를 냈고 RC 에는 요청이 닿지도 않았다. HTTP/2 에서 브라우저가
//   Content-Length(File.size) 만큼 본문을 보내지 못하고 스트림을 끝내면 nginx 가 그렇게 응답한다(서버 curl 재현).
//   폰 갤러리·사진 선택기가 준 File 은 크기 메타데이터만 있고 실제 바이트는 읽을 때 가져오는데(클라우드 최적화 사진,
//   선택기 캐시), 읽기가 실패하거나 짧게 끝나면 전송이 중간에 끊긴다. 데스크톱의 파일 온디맨드(OneDrive 등)도 같은 부류.
//   대책: 보내기 전에 **메모리로 한 번 다 읽어** 길이가 확정된 Blob 으로 보낸다(읽기 실패는 전송 전에 사람이 읽을
//   문구로). 큰 파일(상한 초과)은 메모리에 못 올리니 앞부분만 읽어 접근 가능한지 확인하고 원본 그대로 스트리밍.
export const STABILIZE_MAX_BYTES = 96 * 1024 * 1024;   // 이보다 크면 전량 읽기 대신 앞부분 프로브만 (폰 메모리 보호)
const PROBE_BYTES = 64 * 1024;

/** 파일을 끝까지 읽지 못했을 때 — 호출부가 '네트워크 오류' 가 아니라 '파일을 읽지 못함' 으로 안내한다. */
export class UploadReadError extends Error {
	constructor(msg = 'upload read failed') { super(msg); this.name = 'UploadReadError'; }
}

type BlobLike = { size: number; type: string; arrayBuffer: () => Promise<ArrayBuffer>; slice: (a: number, b: number) => { arrayBuffer: () => Promise<ArrayBuffer> }; name?: string; lastModified?: number };

async function readAll(f: BlobLike): Promise<ArrayBuffer> {
	try { return await f.arrayBuffer(); } catch {
		// 첫 읽기가 실패해도 잠깐 뒤엔 되는 경우가 있다(선택기가 원본을 내려받는 중) — 한 번만 더
		await new Promise((r) => setTimeout(r, 300));
		return f.arrayBuffer();
	}
}

/** 업로드 직전 호출. 상한 이하 = 전량 읽어 새 File(길이 확정) / 초과 = 앞부분 프로브 후 원본. 읽기 실패·길이 불일치 = UploadReadError. */
export async function stabilizeUploadFile<T extends BlobLike>(file: T, maxBytes = STABILIZE_MAX_BYTES): Promise<T | File> {
	if (file.size > maxBytes) {
		try { await file.slice(0, PROBE_BYTES).arrayBuffer(); } catch { throw new UploadReadError('probe failed'); }
		return file;
	}
	let buf: ArrayBuffer;
	try { buf = await readAll(file); } catch { throw new UploadReadError('read failed'); }
	if (buf.byteLength !== file.size) throw new UploadReadError(`size mismatch ${buf.byteLength}/${file.size}`);
	return new File([buf], file.name || 'upload', { type: file.type || '', lastModified: file.lastModified || Date.now() });
}

/** 🚦 동시 업로드 묶음 — 개수 + **크기 합** 상한 (2026-10-27 서버 RC OOM 19회 — 분할 압축 z11~z26(개당 ~400MB)을 4개씩 동시에 올려
 *  RC 6.5 가 본문을 통째로 메모리에 들고 처리하다 10G 상한 초과 → 강제 종료, 재전송마다 다른 RC 가 차례로 죽었다).
 *  종전은 개수(4)만 봤다. 이제 한 묶음 = 최대 maxCount 개이면서 합계 maxBytes 이하, 그보다 큰 파일은 혼자.
 *  사진처럼 작은 파일은 종전처럼 4개씩, 큰 파일은 한 번에 하나. 반환 = 원래 인덱스 묶음(순서 유지). */
export const UPLOAD_BATCH_MAX_BYTES = 200 * 1024 * 1024;
export function planUploadBatches(sizes: readonly number[], maxCount = 4, maxBytes = UPLOAD_BATCH_MAX_BYTES): number[][] {
	const out: number[][] = [];
	let cur: number[] = [];
	let sum = 0;
	sizes.forEach((raw, i) => {
		const s = Math.max(0, Number(raw) || 0);
		if (cur.length && (cur.length >= maxCount || sum + s > maxBytes)) { out.push(cur); cur = []; sum = 0; }
		cur.push(i); sum += s;
	});
	if (cur.length) out.push(cur);
	return out;
}

/** planUploadBatches 대로 묶음씩 실행하고 결과를 **원래 인덱스 순서**로 돌려준다(Promise.allSettled 와 같은 모양). */
export async function settleInUploadBatches<T>(sizes: readonly number[], run: (i: number) => Promise<T>, maxCount = 4, maxBytes = UPLOAD_BATCH_MAX_BYTES): Promise<PromiseSettledResult<T>[]> {
	const out: PromiseSettledResult<T>[] = new Array(sizes.length);
	for (const batch of planUploadBatches(sizes, maxCount, maxBytes)) {
		const part = await Promise.allSettled(batch.map((i) => run(i)));
		part.forEach((r, k) => { out[batch[k]] = r; });
	}
	return out;
}
